import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { MapPin, Plus, Trash2, X, Users, CheckCircle2, Phone, Navigation } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import './BranchManager.css';

const BranchManager = () => {
    const { branches, addBranch, deleteBranch } = useOps();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form, setForm] = useState({ branch_name: '', city: '', address: '', phone: '' });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Backend expects 'branch_name', ensuring strict mapping
            await addBranch(form); // form now has branch_name
            setIsModalOpen(false);
            setForm({ branch_name: '', city: '', address: '', phone: '' });
        } catch (error) {
            console.error("Failed to save branch", error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-container branch-manager-page">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Branch Network</h2>
                    <p className="page-subtitle">Manage your physical locations and service centers</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="btn btn-primary flex items-center gap-2 px-8"
                >
                    <Plus size={20} /> Add New Branch
                </button>
            </div>

            <div className="branch-grid fade-in">
                {branches.map(branch => (
                    <div key={branch.id} className="branch-card">
                        <div className="branch-card-header">
                            <div className="branch-icon-wrap">
                                <MapPin size={28} strokeWidth={2.5} />
                            </div>
                            <button
                                onClick={() => deleteBranch(branch.id)}
                                className="branch-delete-btn"
                                title="Remove Branch"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>

                        <div>
                            <h3 className="branch-title">{branch.name}</h3>
                            <div className="branch-meta">
                                <span className="branch-city-badge">
                                    {branch.city || 'Location'}
                                </span>
                                <p className="branch-address">
                                    {branch.address || 'Address not registered'}
                                </p>
                            </div>
                        </div>

                        <div className="branch-footer">
                            <div>
                                <div className="branch-footer-label">Contact</div>
                                <div className="branch-phone flex items-center gap-2">
                                    <Phone size={12} className="text-emerald-500" />
                                    {branch.phone || 'N/A'}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-emerald-600 font-bold text-[10px] uppercase tracking-widest">
                                <CheckCircle2 size={14} /> Active
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3 className="modal-title">Register Branch</h3>
                            <button onClick={() => setIsModalOpen(false)} className="close-btn">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Branch Identity</label>
                                <input
                                    className="form-input"
                                    placeholder="e.g. Harare North"
                                    value={form.branch_name}
                                    onChange={e => setForm({ ...form, branch_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label className="form-label">City</label>
                                    <input
                                        className="form-input"
                                        placeholder="City"
                                        value={form.city}
                                        onChange={e => setForm({ ...form, city: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Phone</label>
                                    <input
                                        className="form-input"
                                        placeholder="Phone"
                                        value={form.phone}
                                        onChange={e => setForm({ ...form, phone: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Address</label>
                                <textarea
                                    className="form-input"
                                    style={{ minHeight: '100px', resize: 'none' }}
                                    placeholder="Full physical address..."
                                    value={form.address}
                                    onChange={e => setForm({ ...form, address: e.target.value })}
                                />
                            </div>
                            <button
                                type="submit"
                                className="submit-btn flex justify-center items-center gap-2"
                                disabled={saving}
                            >
                                {saving ? <LoadingSpinner size={16} color="white" /> : <Plus size={18} />}
                                {saving ? 'Saving...' : 'Save Branch'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BranchManager;
